import { formatDate, ViewportScroller } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { Subscription, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { addDays } from '../../core/helpers';

import { AccountService, DynamicScriptLoaderService, FileExportService, InvoiceService, ToastService } from '../../core/services';

@Component({
    selector: 'gs-transactions',
    templateUrl: './transactions.component.html',
    standalone: false
})
export class TransactionsComponent implements OnDestroy, OnInit {
  tabs = {
    INCOMING: 0,
    OUTGOING: 1
  };

  selectedTab: number = this.tabs.INCOMING;

  incoming: any[];
  filteredIncoming: any[];
  pageIncoming: number = 1;
  itemsPerPageIncoming: number = 10;
  filterIncoming: string = "all";

  outgoing: any[];
  filteredOutgoing: any[];
  pageOutgoing: number = 1;
  itemsPerPageOutgoing: number = 10;
  filterOutgoing: string = "all";

  pdfScriptsLoaded: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private accountService: AccountService,
    private invoiceService: InvoiceService,
    private toastService: ToastService,
    private fileExportService: FileExportService,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private viewportScroller: ViewportScroller
  ) {

    this.subscriptions.push(
      this.dynamicScriptLoaderService
        .loadMultipleScriptsByName('jspdf', 'xlsx')
        .pipe(mergeMap(() => this.dynamicScriptLoaderService
          .loadScriptByName('jspdf.autotable')))
        .subscribe(() => {
          this.pdfScriptsLoaded = true;
        })
    );

  }

  ngOnInit() {
    this.getTransactions();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getTransactions() {
    this.subscriptions.push(
      this.accountService.getTransactions()
        .pipe(
          catchError(e => {
            //this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          })
        )
        .subscribe((response: any) => {
          this.outgoing = response.outgoing.filter(x => x.amount != 0);
          this.incoming = response.incoming.filter(x => x.amount != 0);
          this.filterIncomingTransactions();
          this.filterOutgoingTransactions();
        })
    );
  }

  onDownloadInvoice(code: string) {
    this.subscriptions.push(
      this.invoiceService.downloadInvoice(code, `factura-giftshare-${code}.pdf`)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          })
        )
        .subscribe(() => { })
    );
  }

  exportAsPdf() {
    switch (this.selectedTab) {
      case this.tabs.INCOMING: {
        const exportData = this.filteredIncoming.map(x => ({
          ...x,
          createdDate: formatDate(x.createdDate, 'dd/MM/yyyy', 'en'),
          walletStatus: this.getTransactionStatusText(x)
        }));

        this.fileExportService.exportAsPdf(
          exportData,
          ['createdDate', 'userName', 'description', 'walletStatus', 'amount'],
          ['Data', 'De la', 'Detalii', 'Status', 'Valoare (lei)'],
          `incasari-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
        );
        break;
      }
      case this.tabs.OUTGOING: {
        const exportData = this.filteredOutgoing.map(x => ({
          ...x,
          createdDate: formatDate(x.createdDate, 'dd/MM/yyyy', 'en'),
          walletStatus: this.getTransactionStatusText(x)
        }));

        this.fileExportService.exportAsPdf(
          exportData,
          ['createdDate', 'userName', 'description', 'walletStatus', 'amount'],
          ['Data', 'Catre', 'Detalii', 'Status', 'Valoare (lei)'],
          `plati-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
        );
        break;
      }
    }
  }

  exportAsExcel() {
    switch (this.selectedTab) {
      case this.tabs.INCOMING: {
        const exportData = this.filteredIncoming.map(x => ({
          ...x,
          createdDate: formatDate(x.createdDate, 'dd/MM/yyyy', 'en'),
          walletStatus: this.getTransactionStatusText(x)
        }));

        this.fileExportService.exportAsExcel(
          exportData,
          ['createdDate', 'userName', 'description', 'walletStatus', 'amount'],
          ['Data', 'De la', 'Detalii', 'Status', 'Valoare (lei)'],
          `incasari-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
        );
        break;
      }
      case this.tabs.OUTGOING: {
        const exportData = this.filteredOutgoing.map(x => ({
          ...x,
          createdDate: formatDate(x.createdDate, 'dd/MM/yyyy', 'en'),
          walletStatus: this.getTransactionStatusText(x)
        }));

        this.fileExportService.exportAsExcel(
          exportData,
          ['createdDate', 'userName', 'description', 'walletStatus', 'amount'],
          ['Data', 'Catre', 'Detalii', 'Status', 'Valoare (lei)'],
          `plati-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
        );
        break;
      }
    }
  }

  onTabSelected(id: number) {
    this.selectedTab = id;
    this.scroll();
  }

  getTransactionDescription(transaction: any): string {
    return transaction.userName === 'GiftShare.ro' || !transaction.eventCode
      ? transaction.description
      : `<a href="/eveniment/${transaction.eventCode}">${transaction.description}</a>`;
  }

  getTransactionStatusText(order: any): string {
    switch (order.walletStatus) {
      case 'Pending': {
        return 'In asteptare';
      }
      case 'Finalized': {
        return 'Finalizata';
      }
      case 'Refunded': {
        return 'Rambursata';
      }
    }

    return '';
  }

  onFilterIncomingChange() {
    this.resetPageIncoming();
    this.filterIncomingTransactions();
    this.scroll();
  }

  onFilterOutgoingChange() {
    this.resetPageOutgoing();
    this.filterOutgoingTransactions();
    this.scroll();
  }

  onPageIncomingChanged(e: number) {
    this.scroll();
  }

  onPageOutgoingChanged(e: number) {
    this.scroll();
  }

  private filterIncomingTransactions() {
    if (!this.incoming) {
      return;
    }

    switch (this.filterIncoming) {
      case 'last-week': {
        const referenceDate = addDays(new Date(), -7);
        this.filteredIncoming = this.incoming.filter((x) => x.createdDate >= referenceDate);
        break;
      }
      case 'last-month': {
        const referenceDate = addDays(new Date(), -30);
        this.filteredIncoming = this.incoming.filter((x) => x.createdDate >= referenceDate);
        break;
      }
      case 'pending': {
        this.filteredIncoming = this.incoming.filter(x => x.walletStatus.toLowerCase() == 'pending');
        break;
      }
      case 'refunded': {
        this.filteredIncoming = this.incoming.filter(x => x.walletStatus.toLowerCase() == 'refunded');
        break;
      }
      case 'finalized': {
        this.filteredIncoming = this.incoming.filter(x => x.walletStatus.toLowerCase() == 'finalized');
        break;
      }
      default: {
        this.filteredIncoming = [...this.incoming];
        break;
      }
    }
  }

  private filterOutgoingTransactions() {
    if (!this.outgoing) {
      return;
    }

    switch (this.filterOutgoing) {
      case 'last-week': {
        const referenceDate = addDays(new Date(), -7);
        this.filteredOutgoing = this.outgoing.filter((x) => x.createdDate >= referenceDate);
        break;
      }
      case 'last-month': {
        const referenceDate = addDays(new Date(), -30);
        this.filteredOutgoing = this.outgoing.filter((x) => x.createdDate >= referenceDate);
        break;
      }
      case 'pending': {
        this.filteredOutgoing = this.outgoing.filter(x => x.walletStatus.toLowerCase() == 'pending');
        break;
      }
      case 'refunded': {
        this.filteredOutgoing = this.outgoing.filter(x => x.walletStatus.toLowerCase() == 'refunded');
        break;
      }
      case 'finalized': {
        this.filteredOutgoing = this.outgoing.filter(x => x.walletStatus.toLowerCase() == 'finalized');
        break;
      }
      default: {
        this.filteredOutgoing = [...this.outgoing];
        break;
      }
    }
  }

  private resetPageIncoming() {
    this.pageIncoming = 1;
  }

  private resetPageOutgoing() {
    this.pageOutgoing = 1;
  }

  private scroll() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }
}
